import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { BodyText, Button, theme, TitleText, useToggleState } from '@sg/garnish';

import { OrderCancellationModal, useOrderCancellationModal } from '../../../../components';
import { useContactUsHandler } from '../../../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const OrderStatusContactUs = (props: OrderStatusContactUsBaseProps) => {
  const { orderId, refetchOrderStatus, restaurantId, canCancel } = props;

  const { handleContactUs } = useContactUsHandler('order-status');

  return (
    <OrderStatusContactUsCard
      onContactUsPress={handleContactUs}
      orderId={orderId}
      refetchOrderStatus={refetchOrderStatus}
      restaurantId={restaurantId}
      canCancel={canCancel}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const OrderStatusContactUsCard = (
  props: OrderStatusContactUsProps & OrderStatusContactUsBaseProps,
) => {
  const { orderId, canCancel, restaurantId, refetchOrderStatus, onContactUsPress } = props;

  const { formatMessage } = useIntl();

  // ─── State ───────────────────────────────────────────────────────────

  const {
    value: shouldHideCancelDuringAsyncCancelation,
    toggleOn: hideCancelDuringAsyncCancelation,
  } = useToggleState();

  const {
    isOrderCancellationModalVisible,
    showOrderCancellationModal,
    hideOrderCancellationModal,
  } = useOrderCancellationModal();

  // ─── Flags ───────────────────────────────────────────────────────────

  const shouldDisplayInAppCancellationOption =
    canCancel && orderId !== undefined && restaurantId !== undefined;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <View style={styles.cardContainer}>
        <TitleText style={styles.cardTitle} sizeMatch={['24']}>
          {formatMessage(messages.orderStatusInfoContactUsHeading)}
        </TitleText>

        <BodyText style={styles.cardBody} sizeMatch={['16']}>
          {formatMessage(messages.orderStatusInfoContactUsSubheading)}
        </BodyText>

        <View style={styles.cardCtaContainer}>
          <Button palette="primary" onPress={onContactUsPress}>
            {formatMessage(messages.orderStatusInfoContactUsCta)}
          </Button>

          {shouldDisplayInAppCancellationOption && !shouldHideCancelDuringAsyncCancelation ? (
            <Button palette="secondary" onPress={showOrderCancellationModal}>
              {formatMessage(messages.orderStatusInfoContactUsCancelOrderCta)}
            </Button>
          ) : null}
        </View>
      </View>

      {shouldDisplayInAppCancellationOption ? (
        <OrderCancellationModal
          isVisible={isOrderCancellationModalVisible}
          orderId={orderId}
          restaurantId={restaurantId}
          onSuccess={refetchOrderStatus}
          onAsyncCancelation={hideCancelDuringAsyncCancelation}
          onRequestClose={hideOrderCancellationModal}
        />
      ) : null}
    </>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  orderStatusInfoContactUsHeading: {
    defaultMessage: 'Need help with your order?',
    description: 'Order Status Info | Contact Us | Heading',
  },
  orderStatusInfoContactUsSubheading: {
    defaultMessage: 'We want to hear from you.',
    description: 'Order Status Info | Contact Us | Subheading',
  },
  orderStatusInfoContactUsCta: {
    defaultMessage: 'Contact us',
    description: 'Order Status Info | Contact Us | Cta',
  },
  orderStatusInfoContactUsCancelOrderCta: {
    defaultMessage: 'Cancel order',
    description: 'Order Status Info | Contact Us | Cancel Order Cta',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  cardContainer: {
    alignItems: 'center',
    borderRadius: theme.radius.medium,
    backgroundColor: theme.colors.QUINOA,
    marginBottom: theme.spacing['4'],
    padding: theme.spacing['6'],
  },
  cardTitle: {
    textAlign: 'center',
    marginBottom: theme.spacing['4'],
  },
  cardBody: {
    textAlign: 'center',
    marginBottom: theme.spacing['4'],
  },
  cardCtaContainer: {
    flexDirection: 'row',
    gap: theme.spacing['4'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type OrderStatusContactUsBaseProps = {
  orderId?: string;
  restaurantId?: string;
  canCancel: boolean;
  refetchOrderStatus: () => void;
};

type OrderStatusContactUsProps = Readonly<{
  onContactUsPress: () => void;
}>;
